import { LoadingIndicator } from "@/components/shared/LoadingIndicator";
import Paragraph from "@/components/shared/text/paragraph";
import { useTranslations } from "@/i18n/utils";
import { tabs } from "@/lib/public-profile/publicProfile-tabs";
import { cn, getActiveTab, getRelativePath } from "@/lib/utils";
import {
  navigationStore,
  startNavigation,
  endNavigation,
} from "@/stores/navigationStore";
import { showEvents, showGallery, showOverview } from "@/stores/tabsStore";
import { useStore } from "@nanostores/react";
import { useEffect, useState } from "react";

interface Props {
  astroUrl: URL;
  defaultScrolled?: boolean;
  lang: "es" | "en";
}

const TabNavigation = ({ astroUrl, defaultScrolled, lang }: Props) => {
  const [mounted, setMounted] = useState(false);
  const $showEvents = useStore(showEvents);
  const $showGallery = useStore(showGallery);
  const $showOverview = useStore(showOverview);
  const navigation = useStore(navigationStore);

  useEffect(() => {
    setMounted(true);

    endNavigation();

    const handlePageShow = () => {
      endNavigation();
    };

    window.addEventListener("pageshow", handlePageShow);
    document.addEventListener("astro:page-load", handlePageShow);

    return () => {
      window.removeEventListener("pageshow", handlePageShow);
      document.removeEventListener("astro:page-load", handlePageShow);
    };
  }, []);

  // Add navigation state cleanup on pathname change
  useEffect(() => {
    endNavigation();
  }, [astroUrl.pathname]);

  if (!astroUrl || !mounted) {
    return null;
  }

  const tabList = tabs().filter((tab) => {
    if (tab.name === "Events") return $showEvents;
    if (tab.name === "Gallery") return $showGallery;
    if (tab.name === "Overview") return $showOverview;
    return false;
  });

  if (tabList.length === 0) {
    return null;
  }

  const currentTab = getActiveTab(astroUrl.pathname);
  const activeTab = tabList.find(
    (tab) => tab.name.toLowerCase() === currentTab?.toLowerCase(),
  );

  const t = useTranslations(lang);

  const handleTabClick = (tabName: string) => {
    startNavigation(tabName.toLowerCase());
  };

  return (
    <>
      {navigation.isNavigating && <LoadingIndicator />}
      <nav
        className="col-span-full md:col-start-1 md:col-end-3"
        aria-label="Tabs"
        role="tablist"
      >
        <div
          className={cn(
            "flex w-full gap-4 sm:justify-start sm:gap-xs",
            $showGallery && $showEvents ? "justify-between" : "justify-start",
          )}
        >
          {tabList.map((tab) => {
            const isActive = activeTab === tab;
            const isTarget = navigation.targetTab === tab.name.toLowerCase();
            return (
              <a
                key={tab.name}
                href={getRelativePath(astroUrl, defaultScrolled, tab.href)}
                onClick={() => handleTabClick(tab.name)}
                role="tab"
                aria-current={isActive ? "page" : undefined}
                className={cn(
                  "relative gap-2 px-4 py-2 transition-all duration-150",
                  isActive && "rounded-full bg-lime-20",
                  isTarget && "opacity-70",
                )}
              >
                <Paragraph
                  weight="medium"
                  className={cn("text-white-80", isActive && "text-lime-100")}
                >
                  {String(t(`publicProfile.tab.${tab.name.toLowerCase()}`))}
                </Paragraph>
                {isTarget && !isActive && (
                  <div className="absolute bottom-0 left-0 h-0.5 w-full animate-pulse bg-lime-100" />
                )}
              </a>
            );
          })}
        </div>
      </nav>
    </>
  );
};

export default TabNavigation;
